import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, Plus } from "lucide-react";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { Button } from "../ui/button";
import { DateGroup } from "./DateGroup";
import { EventCard, type Event, type SeriesInfo } from "./EventCard";
import { EventModal } from "./EventModal";

interface EventListProps {
  events: Event[];
  isLoading?: boolean;
  selectedStyle?: string | null;
  initialEvent?: Event;
  /** Series context keyed by event id, for entries standing in for a series. */
  seriesInfo?: Map<string, SeriesInfo>;
}

export function EventList({ events, isLoading, selectedStyle, initialEvent, seriesInfo }: EventListProps) {
  const { styleMap } = useDanceStyles();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const initialEventOpened = useRef(false);

  // Auto-open shared event from URL
  useEffect(() => {
    if (initialEvent && !initialEventOpened.current) {
      initialEventOpened.current = true;
      setSelectedEvent(initialEvent);
    }
  }, [initialEvent]);

  const groupedEvents = useMemo(() => {
    const groups = new Map<string, Event[]>();
    for (const event of events) {
      const existing = groups.get(event.date) ?? [];
      existing.push(event);
      groups.set(event.date, existing);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="p-5 space-y-3">
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <div className="h-6 w-16 bg-muted rounded-md animate-pulse" />
                  <div className="h-6 w-16 bg-muted rounded-md animate-pulse" />
                </div>
                <div className="h-5 w-20 bg-muted rounded animate-pulse" />
              </div>
              <div className="h-6 w-3/4 bg-muted rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
              <div className="h-4 w-full bg-muted rounded animate-pulse" />
            </div>
            <div className="px-5 py-3 border-t border-border flex gap-2">
              <div className="h-9 w-9 bg-muted rounded-lg animate-pulse" />
              <div className="h-9 w-9 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    const styleName = selectedStyle ? styleMap[selectedStyle]?.name : null;
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
          <Calendar className="w-7 h-7 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-xl text-foreground mb-2">
          {styleName ? `No ${styleName} events` : "No events found"}
        </h3>
        <p className="text-muted-foreground max-w-sm mx-auto mb-6">
          {styleName
            ? `No upcoming ${styleName} events. Try a different style or check back later.`
            : "No upcoming events yet. Be the first to add one!"}
        </p>
        <Button asChild size="lg" variant="contrast">
          <a href="/submit">
            <Plus className="w-4 h-4" />
            Submit an Event
          </a>
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-10">
        {groupedEvents.map(([date, dateEvents]) => (
          <DateGroup key={date} date={date}>
            <div className="space-y-4">
              {dateEvents.map((event) => (
                <EventCard
                  key={event._id}
                  event={event}
                  onClick={() => setSelectedEvent(event)}
                  seriesInfo={seriesInfo?.get(event._id)}
                />
              ))}
            </div>
          </DateGroup>
        ))}
      </div>

      <EventModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </>
  );
}
